import { useQuery } from '@tanstack/react-query';
import { dramatstraAPI } from '../services/api';
import { SearchParams, WatchHistory } from '../types/domain';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await dramatstraAPI.categories.list();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useSeries(params?: SearchParams) {
  return useQuery({
    queryKey: ['series', params?.limit, params?.sort, params?.page],
    queryFn: async () => {
      const response = await dramatstraAPI.series.list(params);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSearchSeries(query: string, params?: SearchParams) {
  return useQuery({
    queryKey: ['series', 'search', query, params?.limit, params?.sort, params?.page],
    queryFn: async () => {
      const response = await dramatstraAPI.series.search(query, params);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!query && query.length > 0,
  });
}

export function useSeriesDetail(id: string) {
  return useQuery({
    queryKey: ['seriesDetail', id],
    queryFn: async () => {
      const response = await dramatstraAPI.series.detail(id);
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!id,
  });
}

export function useEpisodes(seriesId: string) {
  return useQuery({
    queryKey: ['episodes', seriesId],
    queryFn: async () => {
      const response = await dramatstraAPI.episodes.list(seriesId);
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!seriesId,
  });
}

export function useEpisodeDetail(seriesId: string, episodeNumber: number) {
  return useQuery({
    queryKey: ['episode', seriesId, episodeNumber],
    queryFn: async () => {
      const response = await dramatstraAPI.episodes.detail(
        seriesId,
        episodeNumber
      );
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!seriesId && !!episodeNumber,
  });
}

export function useContinueWatching(limit: number = 10) {
  const { isAuthenticated } = useAuth();

  return useQuery<WatchHistory[]>({
    queryKey: ['watch-history', 'continue', limit],
    queryFn: async () => {
      console.log('📺 [useContinueWatching] Buscando histórico de visualizações...');
      const response = await dramatstraAPI.watchHistory.list({
        limit,
      });
      const data = response.data?.data || [];
      console.log('📺 [useContinueWatching] Resposta recebida:', data);
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !!isAuthenticated, // ✅ NÃO faz request se não está autenticado
  });
}

export function useSeriesInWatchlist(seriesId: string) {
  const [isInWatchlist, setIsInWatchlist] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refetchFn, setRefetchFn] = useState<(() => void) | null>(null);

  useEffect(() => {
    if (!seriesId) return;

    const checkWatchlist = async () => {
      setIsLoading(true);
      try {
        const response = await dramatstraAPI.watchlist.list({ limit: 1000 });
        const watchlist = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const found = watchlist.some(item => item.series_id === seriesId);
        setIsInWatchlist(found);
      } catch {
        setIsInWatchlist(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkWatchlist();

    // Retornar função de refetch
    setRefetchFn(() => checkWatchlist);
  }, [seriesId]);

  return { isInWatchlist, isLoading, refetch: refetchFn };
}
